import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ContextProps, createContext } from '../common/contextHelper'
import { LocalStorageItem } from '../common/constants'
import { deleteCookie } from '../common/helpers'
import { authService } from '../services/auth'
import { deviceCache } from '../utils/deviceCache'
import { readingsCacheService } from '../services/cache/readingsCacheService'
import { onAuthError } from '../utils/authEvents'
import { isPublicPath } from '../config/publicRoutes'

interface AuthContextState {
  isAuthorized: boolean | undefined
  authInProgress: boolean
  signOutInProgress: boolean
  error: string
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => void
  completeSignIn: (email: string, accessToken?: string) => void
  resetPassword: (email: string) => Promise<void>

  email: string
  setEmail: (email: string) => void
}

// Paths where a session exists but the user must NOT yet be treated as fully
// signed in -- the app is still mid-flow and will route them onward itself.
const TRANSIENT_AUTH_PATHS = ['/registration/complete', '/auth/callback', '/register', '/reset-password']

// Paths that genuinely mean "registration has started but is not finished", and
// so may persist the registrationPending flag.
//
// Password recovery (/auth/callback, /reset-password) is deliberately NOT in
// this list. Those paths serve existing, fully-registered users, and only
// CompleteProfilePage ever clears the flag -- so stamping it during a password
// reset left the flag set forever. From then on every successful login was
// discarded by the SIGNED_IN handler below and the user was returned to
// /login, with no way out: signOut() (the only other code that clears it)
// early-returns while isAuthorized is false, which the flag itself guarantees.
const REGISTRATION_ENTRY_PATHS = ['/registration/complete', '/register']

const isTransientAuthPath = (path: string): boolean => TRANSIENT_AUTH_PATHS.includes(path)
const isRegistrationEntryPath = (path: string): boolean => REGISTRATION_ENTRY_PATHS.includes(path)

const [Provider, useAuthContext] = createContext<AuthContextState>(module.filename)

export { useAuthContext }

export function AuthProvider({ children }: ContextProps): ReactElement {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient

  // react-router 6.3 hands out a new navigate on every pathname change. The
  // auth effect below must run once per app load -- re-running it refreshed
  // the Supabase session (rotating the refresh token) on every click -- so it
  // and signOut reach navigate through this ref instead of depending on it.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const [isAuthorized, setIsAuthorized] = useState<boolean>()
  const [authInProgress, setAuthInProgress] = useState(false)

  const [email, setEmail] = useState('')
  const [signOutInProgress, setSignOutInProgress] = useState(false)
  const [error, setError] = useState('')

  // Refs for synchronous checks and unmount detection
  const signOutInProgressRef = useRef(false)
  const isMountedRef = useRef(true)
  // Bumped by every explicit sign-in. The startup session check started before
  // it and must not overwrite its result when it resolves late (it is still
  // in flight on /auth/callback while the callback page signs the user in).
  const signInEpochRef = useRef(0)

  useEffect(() => {
    let isActive = true
    isMountedRef.current = true
    const startEpoch = signInEpochRef.current

    const initializeAuth = async () => {
      if (!isActive) return

      const currentPath = window.location.pathname
      const isRegistrationFlow = isTransientAuthPath(currentPath)

      try {
        const storedToken = localStorage.getItem(LocalStorageItem.token)
        const storedEmail = localStorage.getItem(LocalStorageItem.email)

        if (!storedToken && !storedEmail && !isRegistrationFlow) {
          await authService.signOut()
          // A sign-in that finished while this was in flight wins
          if (!isActive || signInEpochRef.current !== startEpoch) return
          setIsAuthorized(false)
          return
        }

        const session = await authService.refreshSession()
        if (!isActive || signInEpochRef.current !== startEpoch) return

        if (session?.user?.email) {
          const registrationPending = localStorage.getItem(LocalStorageItem.registrationPending) === 'true'

          if (isRegistrationFlow) {
            if (isRegistrationEntryPath(currentPath)) {
              localStorage.setItem(LocalStorageItem.registrationPending, 'true')
            }
            setEmail(session.user.email)
            localStorage.setItem(LocalStorageItem.email, session.user.email)
            setIsAuthorized(false)
            return
          }

          if (registrationPending) {
            setEmail(session.user.email)
            localStorage.setItem(LocalStorageItem.email, session.user.email)
            setIsAuthorized(false)
            return
          }

          setIsAuthorized(true)
          setEmail(session.user.email)
          localStorage.setItem(LocalStorageItem.email, session.user.email)
        } else {
          const registrationPending = localStorage.getItem(LocalStorageItem.registrationPending) === 'true'
          if (!isRegistrationFlow && !registrationPending) {
            const storedEmail = localStorage.getItem(LocalStorageItem.email)
            setIsAuthorized(!!storedEmail)
            if (storedEmail) {
              setEmail(storedEmail)
            }
          } else {
            setIsAuthorized(false)
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (!isActive || signInEpochRef.current !== startEpoch) return
        const registrationPending = localStorage.getItem(LocalStorageItem.registrationPending) === 'true'
        if (!isRegistrationFlow && !registrationPending) {
          const storedEmail = localStorage.getItem(LocalStorageItem.email)
          setIsAuthorized(!!storedEmail)
          if (storedEmail) {
            setEmail(storedEmail)
          }
        } else {
          setIsAuthorized(false)
        }
      }
    }

    initializeAuth()

    const authListener = authService.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user?.email) {
        const currentPath = window.location.pathname
        const isRegistrationFlow = isTransientAuthPath(currentPath)

        const registrationPending = localStorage.getItem(LocalStorageItem.registrationPending) === 'true'

        if (isRegistrationFlow) {
          if (isRegistrationEntryPath(currentPath)) {
            localStorage.setItem(LocalStorageItem.registrationPending, 'true')
          }
          setEmail(session.user.email)
          localStorage.setItem(LocalStorageItem.email, session.user.email)
          if (session.accessToken) {
            localStorage.setItem(LocalStorageItem.token, session.accessToken)
          }
          return
        }

        if (registrationPending) {
          setEmail(session.user.email)
          localStorage.setItem(LocalStorageItem.email, session.user.email)
          if (session.accessToken) {
            localStorage.setItem(LocalStorageItem.token, session.accessToken)
          }
          return
        }

        setIsAuthorized(true)
        setEmail(session.user.email)
        localStorage.setItem(LocalStorageItem.email, session.user.email)
        if (session.accessToken) {
          localStorage.setItem(LocalStorageItem.token, session.accessToken)
        }
      } else if (event === 'TOKEN_REFRESHED' && session?.accessToken) {
        localStorage.setItem(LocalStorageItem.token, session.accessToken)
      } else if (event === 'SIGNED_OUT') {
        // Read before it is cleared: the deep link below belongs to this user
        const signedOutEmail = localStorage.getItem(LocalStorageItem.email)
        setIsAuthorized(false)
        setEmail('')
        // Expiry and revocation land here without going through signOut(), so
        // drop the previous user's cached data here too
        queryClientRef.current.clear()
        Promise.all([deviceCache.clearAll(), readingsCacheService.clearAllCache()]).catch(() => {})
        localStorage.removeItem(LocalStorageItem.email)
        localStorage.removeItem(LocalStorageItem.token)
        localStorage.removeItem(LocalStorageItem.registrationPending)
        // Don't yank logged-out visitors off public pages (e.g. legal docs).
        // This event also fires for fresh visitors via initializeAuth's signOut().
        if (!isPublicPath(window.location.pathname)) {
          // Remember where they were so signing back in returns them there --
          // but only them: LoginPage drops the link if someone else signs in.
          const { pathname, search, hash } = window.location
          navigateRef.current('/login', { state: { from: { pathname, search, hash }, fromEmail: signedOutEmail } })
        }
      }
    })

    return () => {
      isActive = false
      isMountedRef.current = false
      authListener.unsubscribe()
    }
  }, [])

  const signOut = useCallback(async () => {
    // Prevent overlapping signOut calls. Deliberately does NOT bail out when
    // isAuthorized is false: signOut is the only path that clears persisted
    // auth state, so refusing to run in that state made a bad flag or stale
    // token unrecoverable without devtools. Re-running it is idempotent.
    if (signOutInProgressRef.current) {
      return
    }

    signOutInProgressRef.current = true
    setSignOutInProgress(true)

    try {
      // Clear localStorage tokens FIRST to prevent Supabase from attempting re-auth with stale tokens
      localStorage.removeItem(LocalStorageItem.username)
      localStorage.removeItem(LocalStorageItem.token)
      localStorage.removeItem(LocalStorageItem.email)
      localStorage.removeItem(LocalStorageItem.registrationPending)
      deleteCookie('token')

      // Update state before calling Supabase to prevent auth state listeners from triggering loops
      setEmail('')
      setIsAuthorized(false)

      // Now safely call Supabase signOut and clear caches
      await authService.signOut()
      await Promise.all([deviceCache.clearAll(), readingsCacheService.clearAllCache()])
      // Drop every cached query so the next user to sign in on this tab never
      // sees the previous user's data (e.g. their Reports access answer).
      queryClient.clear()

      navigateRef.current('/login')
    } catch (error) {
      console.error('Error signing out:', error)
      // Caches may not have cleared, try again
      await Promise.all([deviceCache.clearAll(), readingsCacheService.clearAllCache()]).catch(() => {})
      queryClient.clear()
      navigateRef.current('/login')
    } finally {
      signOutInProgressRef.current = false
      if (isMountedRef.current) {
        setSignOutInProgress(false)
      }
    }
  }, [queryClient])

  useEffect(() => {
    const handleAuthError = (payload: { type: string; statusCode: number; message: string; url?: string }) => {
      // Skip if already signed out, signing out, or not authorized
      if (!isAuthorized || signOutInProgress) {
        return
      }

      // 403 Forbidden = permission denied for specific resource, NOT an auth failure
      // Just show toast, don't log out
      if (payload.type === 'auth:forbidden') {
        toast.error('Access denied: You do not have permission to view this resource.')
        return
      }

      // 401 Unauthorized or account deleted = actual auth failure, log out
      let toastMessage = 'Session expired'

      if (payload.type === 'auth:unauthorized') {
        toastMessage = 'Session expired. Please log in again.'
      } else if (payload.type === 'auth:deleted') {
        toastMessage = 'Account no longer exists.'
      }

      toast.error(toastMessage)

      signOut()
    }

    const unsubscribe = onAuthError(handleAuthError)

    return () => {
      unsubscribe()
    }
  }, [isAuthorized, signOutInProgress, signOut])

  // Called by flows that establish a session outside signIn -- the emailed
  // magic-link/invite callback and the end of registration -- right before
  // they navigate into the app. SIGNED_IN fires while those flows are still on
  // a transient path, so nothing else marks the user signed in.
  const completeSignIn = useCallback((userEmail: string, accessToken?: string) => {
    signInEpochRef.current += 1
    setEmail(userEmail)
    localStorage.setItem(LocalStorageItem.email, userEmail)
    if (accessToken) {
      localStorage.setItem(LocalStorageItem.token, accessToken)
    }
    setIsAuthorized(true)
  }, [])

  async function signIn(email: string, password: string) {
    signInEpochRef.current += 1
    try {
      setError('')
      setAuthInProgress(true)
      setIsAuthorized(false)

      // Self-heal a stale registrationPending flag. Anyone signing in here with
      // a password is, by definition, not part-way through registration: an
      // invited user who has not finished has no password yet and arrives via
      // the emailed link, never this form. Without this, a user left holding
      // the flag by an earlier password reset is locked out permanently --
      // their login succeeds and is then thrown away.
      localStorage.removeItem(LocalStorageItem.registrationPending)

      const previousEmail = localStorage.getItem(LocalStorageItem.email)
      if (previousEmail && previousEmail !== email) {
        await deviceCache.clearDevices(previousEmail)
      }

      const { accessToken, user } = await authService.loginWithSupabase(email, password)
      localStorage.setItem(LocalStorageItem.token, accessToken)

      const userEmail = user?.email || email
      setEmail(userEmail)
      localStorage.setItem(LocalStorageItem.email, userEmail)

      setIsAuthorized(true)
    } catch (error: any) {
      setIsAuthorized(false)
      setError(error.response?.data?.error || error.message || 'Login failed')
    } finally {
      setAuthInProgress(false)
    }
  }

  async function resetPassword(email: string): Promise<void> {
    try {
      setError('')
      await authService.resetPasswordForEmail(email)
    } catch (error: any) {
      setError(error.message || 'Password reset failed')
      throw error
    }
  }

  return (
    <Provider
      value={{
        isAuthorized,
        authInProgress,
        signOutInProgress,
        error,
        signIn,
        signOut,
        completeSignIn,
        resetPassword,

        email,
        setEmail,
      }}
    >
      {children}
    </Provider>
  )
}
